import React, { useMemo, useState } from 'react';
import '../../css/modal.css';
import { searchInsurance } from '../../api/Insurance';
import { getDatePlusDays } from '../../utils/date';

type Addon = { name: string; price: number; currency: string; description: string };

type InsurancePlan = Record<string, any>;

const planCoverageOptions = [
  { label: 'US', value: 1 },
  { label: 'Non US', value: 2 },
  { label: 'WorldWide', value: 3 },
  { label: 'India', value: 4 },
  { label: 'Asia', value: 5 },
  { label: 'Canada', value: 6 },
  { label: 'Australia', value: 7 },
  { label: 'Schengen Countries', value: 8 },
];

const planTypeOptions = [
  { label: 'Single Trip', value: 1 },
  { label: 'AMT - Annual multiple entry', value: 2 },
];

const InsuranceAddonModal: React.FC<{
  currency: 'INR' | 'USD';
  travelStartDate: string;
  nightsCount: number;
  noOfPax: number;
  onClose: () => void;
  onSave: (addon: Addon) => void;
}> = ({ currency, travelStartDate, nightsCount, noOfPax, onClose, onSave }) => {
  const [planCoverage, setPlanCoverage] = useState(planCoverageOptions[0].value);
  const [planType, setPlanType] = useState(planTypeOptions[0].value);
  const [paxAgesRaw, setPaxAgesRaw] = useState('');
  const [plans, setPlans] = useState<InsurancePlan[]>([]);
  const [expandedCoverage, setExpandedCoverage] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currencySymbol = currency === 'USD' ? '$' : '₹';
  const effectiveStartDate = travelStartDate || getDatePlusDays(new Date().toISOString(), 30);
  const travelEndDate = effectiveStartDate ? getDatePlusDays(effectiveStartDate, Math.max(0, nightsCount)) : '';
  const derivedPlanCategory = planCoverage === 4 ? 1 : 2;

  const planCategoryLabel = derivedPlanCategory === 1 ? 'Domestic' : 'Overseas';
  const planCoverageLabel = useMemo(
    () => planCoverageOptions.find((o) => o.value === planCoverage)?.label || String(planCoverage),
    [planCoverage]
  );
  const planTypeLabel = useMemo(
    () => planTypeOptions.find((o) => o.value === planType)?.label || String(planType),
    [planType]
  );

  const extractName = (plan: InsurancePlan) =>
    plan.planName || plan.plan_name || plan.name || plan.plan || 'Insurance Plan';
  const extractPrice = (plan: InsurancePlan) =>
    Number(plan?.price?.amount ?? plan.price ?? plan.premium ?? plan.total_premium ?? plan.amount ?? plan.totalAmount ?? 0);
  const extractCurrency = (plan: InsurancePlan) =>
    plan?.price?.currency || plan.currency || plan.price_currency || currency;
  const extractPolicyStart = (plan: InsurancePlan) => plan.policyStartDate || plan.policy_start_date || '';
  const extractPolicyEnd = (plan: InsurancePlan) => plan.policyEndDate || plan.policy_end_date || '';
  const extractCoverageDetails = (plan: InsurancePlan) =>
    Array.isArray(plan.coverageDetails) ? plan.coverageDetails : [];

  const formatDate = (value: string) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString();
  };

  const parsePaxAges = (raw: string): number[] => (
    raw
      .split(',')
      .map((item) => Number(item.trim()))
      .filter((val) => !Number.isNaN(val) && val > 0)
  );

  const handleSearch = async () => {
    const ages = parsePaxAges(paxAgesRaw);
    if (!effectiveStartDate || !travelEndDate) {
      setError('Travel dates are required.');
      return;
    }
    if (ages.length === 0) {
      setError('Enter pax ages separated by commas.');
      return;
    }
    if (ages.length !== noOfPax) {
      setError(`Enter ages for ${noOfPax} pax.`);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const results = await searchInsurance({
        planCategory: derivedPlanCategory,
        planType,
        planCoverage,
        travelStartDate: effectiveStartDate,
        travelEndDate,
        noOfPax,
        paxAge: ages,
      });
      setPlans(Array.isArray(results) ? results : []);
      setExpandedCoverage({});
    } catch (err: any) {
      setError(err?.message || 'Failed to search insurance plans.');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const addPlan = (plan: InsurancePlan) => {
    const name = extractName(plan);
    const price = extractPrice(plan);
    const planCurrency = extractCurrency(plan);
    const policyStart = extractPolicyStart(plan);
    const policyEnd = extractPolicyEnd(plan);
    const description = [
      `Plan: ${name}`,
      `Category: ${planCategoryLabel}`,
      `Coverage: ${planCoverageLabel}`,
      `Type: ${planTypeLabel}`,
      policyStart && policyEnd ? `Policy: ${formatDate(policyStart)} → ${formatDate(policyEnd)}` : '',
    ].join(' • ');

    onSave({
      name: 'Insurance',
      price,
      currency: planCurrency,
      description,
    });
  };

  const toggleCoverage = (key: string) => {
    setExpandedCoverage((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2>Add Insurance</h2>

        <div className="package-editor-row">
          <label>
            Coverage category
            <select value={planCoverage} onChange={(e) => setPlanCoverage(Number(e.target.value))}>
              {planCoverageOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <div>
            <div className="package-editor-label">Plan Category</div>
            <div className="package-editor-small-note">{planCategoryLabel} (auto)</div>
          </div>
        </div>

        <div className="package-editor-row">
          <div>
            <div className="package-editor-label">Plan Type</div>
            <div className="package-editor-checklist">
              {planTypeOptions.map((opt) => (
                <label key={opt.value} className="package-editor-check-item">
                  <input
                    type="checkbox"
                    checked={planType === opt.value}
                    onChange={() => setPlanType(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
          <label>
            Pax Ages (comma separated)
            <input
              value={paxAgesRaw}
              onChange={(e) => setPaxAgesRaw(e.target.value)}
              placeholder="30, 28"
            />
          </label>
        </div>

        <div className="package-editor-small-note">No. of pax: {noOfPax}</div>
        <div className="package-editor-small-note">
          Travel dates: {formatDate(effectiveStartDate)} → {formatDate(travelEndDate)} (auto)
        </div>

        {error && <div className="package-editor-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching…' : 'Search Plans'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>Close</button>
        </div>

        <div className="insurance-plan-list">
          {plans.length === 0 && !loading && (
            <div className="package-editor-small-note">No plans found.</div>
          )}
          {plans.map((plan, idx) => {
            const name = extractName(plan);
            const price = extractPrice(plan);
            const planCurrency = extractCurrency(plan);
            const symbol = planCurrency === 'USD' ? '$' : '₹';
            const policyStart = extractPolicyStart(plan);
            const policyEnd = extractPolicyEnd(plan);
            const coverageItems = extractCoverageDetails(plan);
            const planKey = String(plan.resultIndex ?? idx);
            const isExpanded = !!expandedCoverage[planKey];
            const visibleCoverage = isExpanded ? coverageItems : coverageItems.slice(0, 5);
            const hiddenCount = Math.max(0, coverageItems.length - visibleCoverage.length);
            return (
              <div key={`${name}-${idx}`} className="insurance-plan-card">
                <div>
                  <div className="insurance-plan-title">{name}</div>
                  <div className="insurance-plan-details">
                    
                    {policyStart && policyEnd && (
                      <div className="insurance-plan-detail">
                        Policy: {formatDate(policyStart)} → {formatDate(policyEnd)}
                      </div>
                    )}
                    {coverageItems.length > 0 && (
                      <>
                        <ul className="insurance-plan-coverage">
                          {visibleCoverage.map((item: any, cidx: number) => (
                            <li key={`${name}-cov-${cidx}`}>
                              <span className="insurance-plan-coverage-name">{item.coverage}</span>
                              {item.sumInsured ? (
                                <span className="insurance-plan-coverage-sum">{item.sumInsured}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                        {hiddenCount > 0 && (
                          <button
                            type="button"
                            className="insurance-plan-toggle"
                            onClick={() => toggleCoverage(planKey)}
                          >
                            {isExpanded ? 'View less' : `View more (${hiddenCount})`}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="insurance-plan-actions">
                  <div className="insurance-plan-price">{symbol}{Number(price || 0).toLocaleString('en-IN')}</div>
                  <button type="button" className="package-editor-btn-ghost-small" onClick={() => addPlan(plan)}>
                    Add
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {plans.length > 0 && (
          <div className="package-editor-small-note" style={{ marginTop: 8 }}>
            Prices shown in {currencySymbol} when available.
          </div>
        )}
      </div>
    </div>
  );
};

export default InsuranceAddonModal;
